import { useState, useMemo, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  AppTextField,
  AppDialog,
  AppButton,
  AppSnackbar,
  InputAdornment,
  Search as SearchIcon,
} from '../../components/ui';
import { UserStatus, UserRole } from '../../types/ticket';
import type { User, UserFilters, UserRole as UserRoleType } from '../../types/ticket';
import { Department } from '../../types/knowledgeBase';
import { UserCard } from './UserCard';
import { AddUserCard } from './AddUserCard';
import { UserDialog } from './UserDialog';
import { StatsView } from './StatsView';
import { TechStatusView } from './TechStatusView';
import { useUserRepository, useTechRepository } from '../../data/hooks';
import { useAuth } from '../../contexts/AuthContext';
import './AdminView.css';

type AdminTabType = 'users' | 'techstatus' | 'stats';

interface TabConfig {
  id: AdminTabType;
  path: string;
  label: string;
  count?: number;
}

function getTabFromPath(pathname: string): AdminTabType {
  if (pathname.includes('/admin/tech-status')) return 'techstatus';
  if (pathname.includes('/admin/stats')) return 'stats';
  return 'users';
}

function getPathFromTab(tab: AdminTabType): string {
  switch (tab) {
    case 'techstatus':
      return '/admin/tech-status';
    case 'stats':
      return '/admin/stats';
    case 'users':
    default:
      return '/admin/users';
  }
}

export function AdminView() {
  const location = useLocation();
  const navigate = useNavigate();
  const userRepo = useUserRepository();
  const techRepo = useTechRepository();
  const { getUserId } = useAuth();

  // Derive current tab from URL - this is the source of truth
  const selectedTab = getTabFromPath(location.pathname);

  // Get current user ID from auth context
  const currentUserId = getUserId();

  const [users, setUsers] = useState<User[]>([]);
  const [techCount, setTechCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState<UserFilters>({
    roles: [],
    statuses: [],
    searchQuery: '',
  });
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Load users and tech count from repository
  useEffect(() => {
    let mounted = true;

    async function loadData() {
      try {
        const [loadedUsers, loadedTechs] = await Promise.all([
          userRepo.getAll(),
          techRepo.getAll(),
        ]);
        if (mounted) {
          setUsers(loadedUsers);
          setTechCount(loadedTechs.length);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Failed to load admin data:', error);
        if (mounted) {
          setIsLoading(false);
        }
      }
    }

    loadData();

    return () => {
      mounted = false;
    };
  }, [userRepo, techRepo]);

  const tabs: TabConfig[] = useMemo(
    () => [
      { id: 'users', path: '/admin/users', label: 'User Management', count: users.length },
      { id: 'techstatus', path: '/admin/tech-status', label: 'Tech Status', count: techCount },
      { id: 'stats', path: '/admin/stats', label: 'Stats' },
    ],
    [users.length, techCount]
  );

  const handleTabChange = useCallback(
    (tab: AdminTabType) => {
      const path = getPathFromTab(tab);
      navigate(path);
    },
    [navigate]
  );

  const getUserTicketCount = useCallback(
    async (userId: string): Promise<number> => {
      const tech = await techRepo.getByUserId(userId);
      return tech?.currentLoad ?? 0;
    },
    [techRepo]
  );

  const getUserLocation = useCallback(
    async (userId: string): Promise<string> => {
      const user = users.find((u) => u.id === userId);
      if (user?.location) return user.location;
      const tech = await techRepo.getByUserId(userId);
      return tech?.location ?? 'Not Assigned';
    },
    [users, techRepo]
  );

  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      if (filters.searchQuery) {
        const query = filters.searchQuery.toLowerCase();
        const matchesSearch =
          user.name.toLowerCase().includes(query) ||
          user.email.toLowerCase().includes(query) ||
          user.role.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }
      if (filters.roles.length > 0 && !filters.roles.includes(user.role)) return false;
      if (filters.statuses.length > 0 && !filters.statuses.includes(user.status)) return false;
      return true;
    });
  }, [users, filters]);

  const handleEditUser = useCallback((user: User) => {
    setSelectedUser(user);
    setUserDialogOpen(true);
  }, []);

  const handleDeleteClick = useCallback((user: User) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!userToDelete) return;

    // Optimistic delete
    setUsers((prev) => prev.filter((u) => u.id !== userToDelete.id));
    setSnackbar({
      open: true,
      message: `User ${userToDelete.name} deleted successfully`,
      severity: 'success',
    });
    setDeleteDialogOpen(false);

    // Persist to database
    const success = await userRepo.delete(userToDelete.id);
    if (!success) {
      // Restore on failure
      setUsers((prev) => [...prev, userToDelete]);
      setSnackbar({
        open: true,
        message: 'Failed to delete user',
        severity: 'error',
      });
    }

    setUserToDelete(null);
  }, [userToDelete, userRepo]);

  const handleSaveUser = useCallback(
    async (userData: Partial<User>) => {
      if (selectedUser) {
        // Update existing user
        const updatedUser = { ...selectedUser, ...userData, updatedAt: new Date() };
        setUsers((prev) =>
          prev.map((u) => (u.id === selectedUser.id ? updatedUser : u))
        );
        setSnackbar({
          open: true,
          message: `User ${userData.name} updated`,
          severity: 'success',
        });

        // Persist to database
        userRepo.update(selectedUser.id, userData).catch((error) => {
          console.error('Failed to update user:', error);
        });
      } else {
        // Create new user
        const role: UserRoleType = userData.role ?? UserRole.T1_TECH;
        const newUserData = {
          email: userData.email ?? '',
          name: userData.name ?? '',
          role,
          department: Department.IT, // Default to IT department
          status: userData.status ?? UserStatus.ACTIVE,
          location: userData.location,
        };

        try {
          const newUser = await userRepo.create(newUserData);
          setUsers((prev) => [...prev, newUser]);
          setSnackbar({
            open: true,
            message: `User ${newUser.name} created`,
            severity: 'success',
          });
        } catch (error) {
          console.error('Failed to create user:', error);
          setSnackbar({
            open: true,
            message: 'Failed to create user',
            severity: 'error',
          });
        }
      }
      setUserDialogOpen(false);
      setSelectedUser(null);
    },
    [selectedUser, userRepo]
  );

  const handleAddUser = useCallback(() => {
    setSelectedUser(null);
    setUserDialogOpen(true);
  }, []);

  const handleCloseUserDialog = useCallback(() => {
    setUserDialogOpen(false);
    setSelectedUser(null);
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  }, []);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters((prev) => ({ ...prev, searchQuery: e.target.value }));
  }, []);

  if (isLoading) {
    return null;
  }

  return (
    <Box className="admin-view">
      <Box className="admin-view__container">
        <Box className="admin-view__header-section">
          <Box>
            <Typography variant="h5" component="h2" className="admin-view__title">
              Admin
              <span className="admin-view__count">{users.length}</span>
            </Typography>
            <Typography variant="body2" className="admin-view__subtitle">
              Manage your team members, roles, permissions, and view statistics
            </Typography>
          </Box>
        </Box>

        <Box className="admin-view__tabs" role="tablist" aria-label="Admin sections">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={selectedTab === tab.id}
              aria-controls={`admin-panel-${tab.id}`}
              className={`admin-view__tab ${selectedTab === tab.id ? 'admin-view__tab--active' : ''}`}
              onClick={() => handleTabChange(tab.id)}
            >
              <span className="admin-view__tab-label">{tab.label}</span>
              {tab.count !== undefined && (
                <span className="admin-view__tab-count">{tab.count}</span>
              )}
            </button>
          ))}
          <div className="admin-view__tab-indicator" data-tab={selectedTab} />
        </Box>

        <Box
          role="tabpanel"
          id={`admin-panel-${selectedTab}`}
          aria-labelledby={`admin-tab-${selectedTab}`}
        >
          {selectedTab === 'users' && (
            <Box className="admin-view__users-content">
              <Box className="admin-view__search-section">
                <AppTextField
                  placeholder="Search by name, email, or role..."
                  value={filters.searchQuery}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  fullWidth
                  size="medium"
                  sx={{
                    maxWidth: '600px',
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'background.paper',
                    },
                  }}
                />
              </Box>

              <Box className="admin-view__results-section">
                <Typography variant="body2" color="text.secondary">
                  Showing {filteredUsers.length} of {users.length}{' '}
                  {users.length === 1 ? 'user' : 'users'}
                </Typography>
              </Box>

              <Box className="admin-view__grid">
                {filteredUsers.map((user) => (
                  <Box key={user.id} className="admin-view__grid-item">
                    <UserCard
                      user={user}
                      ticketsCount={0}
                      location={user.location ?? 'Not Assigned'}
                      onEdit={handleEditUser}
                      onDelete={handleDeleteClick}
                      isCurrentUser={user.id === currentUserId}
                    />
                  </Box>
                ))}

                <Box className="admin-view__grid-item">
                  <AddUserCard onClick={handleAddUser} />
                </Box>
              </Box>
            </Box>
          )}

          {selectedTab === 'techstatus' && <TechStatusView currentUserId={currentUserId} />}

          {selectedTab === 'stats' && <StatsView />}
        </Box>

        <UserDialog
          open={userDialogOpen}
          user={selectedUser}
          onClose={handleCloseUserDialog}
          onSave={handleSaveUser}
        />

        <AppDialog
          open={deleteDialogOpen}
          onClose={handleCloseDeleteDialog}
          maxWidth="xs"
          fullWidth
          title="Delete User"
          actions={
            <>
              <AppButton variant="ghost" onClick={handleCloseDeleteDialog}>
                Cancel
              </AppButton>
              <AppButton variant="danger" onClick={handleDeleteConfirm}>
                Delete
              </AppButton>
            </>
          }
        >
          <Typography>
            Are you sure you want to delete user <strong>{userToDelete?.name}</strong>? This action
            cannot be undone.
          </Typography>
        </AppDialog>

        <AppSnackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          message={snackbar.message}
          severity={snackbar.severity}
        />
      </Box>
    </Box>
  );
}
